import React from 'react';

import { Button, Tooltip } from '@components/index';
import { Tag } from 'types/lead-management';

import { deriveDarkColor } from '../utils/common';

interface LeadTagsProps {
  handleAddTagButtonClick?: () => void;
  className?: string;
  tags?: Tag[];
  showAddButton?: boolean;
  showAllTags?: boolean;
}

const MAX_VISIBLE = 2;

const LeadTags: React.FC<LeadTagsProps> = ({
  tags,
  handleAddTagButtonClick,
  className = '',
  showAddButton = true,
  showAllTags = false
}) => {
  const visibleTags = showAllTags ? tags : tags?.slice(0, MAX_VISIBLE);
  const hiddenTags = tags?.slice(MAX_VISIBLE);
  const hiddenTagsTooltipId = `tags-${hiddenTags
    ?.map((tag) => tag.id)
    .join(',')}`;
  const hiddenTagsTooltipClassName =
    '!bg-white !shadow-[0_4px_10px_0px_rgba(0,0,0,0.15)]';

  let extraCount = 0;
  if (!showAllTags) {
    if (tags?.length) {
      extraCount = tags.length - MAX_VISIBLE;
    }
  }

  const addButtonClick = (evt: React.MouseEvent<HTMLButtonElement>) => {
    handleAddTagButtonClick?.();
    evt.stopPropagation();
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {visibleTags?.map((tag) => (
        <span
          key={tag.id}
          className="!text-14 truncate rounded-full px-4 py-1 font-normal"
          style={{
            background: tag.color || '#e5e7eb',
            color: deriveDarkColor(tag.color || '#e5e7eb'),
            maxWidth: 'fit-content',
            fontSize: '14px'
          }}
          data-tooltip-id={tag.name.length > 7 ? `${tag.id}-tag` : undefined}
          title={tag.name}
        >
          {tag.name}
        </span>
      ))}
      {extraCount > 0 && (
        <>
          <div
            className="text-12 rounded-full bg-[#E8FFF4] px-4 py-1 font-normal text-primary"
            style={{
              fontSize: '14px'
            }}
            data-tooltip-id={hiddenTagsTooltipId}
          >
            {`+${extraCount}`}
          </div>
          <Tooltip
            id={hiddenTagsTooltipId}
            className={`${hiddenTagsTooltipClassName} !max-w-72`}
          >
            <div className="!text-14 flex flex-col gap-1">
              {hiddenTags?.map((tag) => (
                <div
                  className="!text-12 rounded-full px-2 py-1"
                  key={tag.id}
                  style={{
                    background: tag.color || '#e5e7eb',
                    color: deriveDarkColor(tag.color || '#e5e7eb')
                  }}
                >
                  {tag.name}
                </div>
              ))}
            </div>
          </Tooltip>
        </>
      )}
      {showAddButton && (
        <Button
          label="Add Tags"
          handleClick={addButtonClick}
          variant="tertiary"
          size="small"
          className="!h-9 !rounded-lg border-[#E2E8F0] !bg-white !px-2 !py-1.5 hover:!bg-gray-50"
          labelStyle="!font-medium !text-sm !text-[#333333]"
          leftIconName="add-grey"
          leftIconStyle="!size-3"
        />
      )}
    </div>
  );
};

export default LeadTags;
